/**
 * Narrow the untyped error our axios base queries return
 * (`{ status?: number, data: unknown }`, see utils/axiosBaseQuery.ts) to its
 * HTTP status code without `as any`. Returns undefined for non-HTTP errors
 * (network failures, aborts) so callers fall back to a generic message.
 */
export const getApiErrorStatus = (error: unknown): number | undefined => {
    if (typeof error === 'object' && error !== null && 'status' in error) {
        const { status } = error as { status: unknown };
        return typeof status === 'number' ? status : undefined;
    }
    return undefined;
};

/**
 * A string field of the error body, e.g. the machine-readable `code` a
 * refusal carries (`invoiced_booking`). Undefined when absent or not a string.
 */
export const getApiErrorField = (error: unknown, field: string): string | undefined => {
    if (typeof error !== 'object' || error === null || !('data' in error)) return undefined;
    const { data } = error as { data: unknown };
    if (typeof data !== 'object' || data === null || !(field in data)) return undefined;
    const value = (data as Record<string, unknown>)[field];
    return typeof value === 'string' && value.trim() ? value : undefined;
};

/** Longest reason we put in a snackbar before cutting it off. */
const MAX_REASON_LENGTH = 200;

const readErrorPayload = (error: unknown): string | undefined => {
    if (typeof error !== 'object' || error === null || !('data' in error)) {
        return undefined;
    }
    // Axios failures without a response (network error, abort) carry the
    // axios message string in `data`; a real API failure carries the JSON body.
    const { data } = error as { data: unknown };
    if (typeof data === 'string') {
        return data;
    }
    if (typeof data === 'object' && data !== null && 'message' in data) {
        const { message } = data as { message: unknown };
        return typeof message === 'string' ? message : undefined;
    }
    return undefined;
};

/**
 * Extract the backend's own explanation from an axios base-query error, for
 * failures where a generic "please try again" is not enough to act on — a
 * refused hard delete, for instance, where the reason is a specific database
 * constraint the operator otherwise never gets to see.
 *
 * Returns the LAST non-empty line, not the first: the new backend's error
 * handler forwards `err.message` verbatim (see
 * new-backend/admin/src/middlewares/errorHandler.ts), and a Prisma error puts
 * its invocation preamble and source excerpt first and the actual reason
 * ("Foreign key constraint violated on the constraint: `…_fkey`") last. Our own
 * `res.json({ message })` strings are single-line and pass through unchanged.
 */
export const getApiErrorMessage = (error: unknown): string | undefined => {
    const payload = readErrorPayload(error);
    if (!payload) return undefined;

    const lines = payload
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
    const reason = lines[lines.length - 1];
    if (!reason) return undefined;

    return reason.length > MAX_REASON_LENGTH
        ? `${reason.slice(0, MAX_REASON_LENGTH)}…`
        : reason;
};
